import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { getCatalogRegistration } from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import { getCountDown } from '@/redux/modules/bidCountDown';
import { Lock, LockOpen } from '@liveauctioneers/hammer-ui-core/icons';
import { useAppSelector } from '@/redux/hooks';
import BidCountDown from '@/components/BidCountDown/BidCountDown';
import FormattedCurrency from '@/components/Formatted/FormattedCurrency';
import styled from 'styled-components';

type Props = {
    biddingLocked: boolean;
    catalogId: number;
    currency: string;
    highBidEstimate: number;
    isTimedAuction: boolean;
    itemId: number;
    lowBidEstimate: number;
};

const PriceEstimateRange = ({
    biddingLocked,
    catalogId,
    currency,
    highBidEstimate,
    isTimedAuction,
    itemId,
    lowBidEstimate,
}: Props) => {
    const timeStamp = useAppSelector((state) => getCountDown(state, itemId));
    const catalogRegistration = useAppSelector((state) => getCatalogRegistration(state, catalogId));

    const showCountDown = isTimedAuction && Boolean(timeStamp) && catalogRegistration.approved;
    const { approved, limited } = catalogRegistration;

    let lockActionTextId = 'bidding_console.console.lockText.unlocked';
    let lockIcon = (
        <LockOpen
            size="xs"
            style="fill"
        />
    );

    if (biddingLocked) {
        lockActionTextId = 'bidding_console.console.lockText.locked';
        lockIcon = (
            <Lock
                size="xs"
                style="fill"
            />
        );
    }

    const estimateRange = {
        high: (
            <FormattedCurrency
                key="price-estimate-range-high"
                noFraction
                sourceCurrency={currency}
                value={highBidEstimate}
            />
        ),
        low: (
            <FormattedCurrency
                key="price-estimate-range-low"
                noFraction
                sourceCurrency={currency}
                value={lowBidEstimate}
            />
        ),
    };

    return (
        <PriceEstimateRangeContainer>
            {showCountDown ? (
                <BidCountDown timeStamp={timeStamp}>
                    <Estimate>
                        <FormattedMessage
                            id="bidding_console.console.description.estimate_range"
                            values={estimateRange}
                        />
                    </Estimate>
                </BidCountDown>
            ) : (
                <EstimateNameContainer>
                    <Estimate aria-label="price range estimate">
                        <FormattedMessage
                            id="bidding_console.console.description.estimate_range"
                            values={estimateRange}
                        />
                    </Estimate>
                    {(approved || limited) && (
                        <span className="flex items-center">
                            {lockIcon}
                            <LockText>
                                <FormattedMessage id={lockActionTextId} />
                            </LockText>
                        </span>
                    )}
                </EstimateNameContainer>
            )}
        </PriceEstimateRangeContainer>
    );
};

export default PriceEstimateRange;

const PriceEstimateRangeContainer = styled.div`
    display: flex;
    margin: 0 8px 0 0;
    float: left;
    justify-content: center;
    font-size: 14px;
    line-height: 16px;
    letter-spacing: 0.5px;
    color: ${({ theme }) => theme.colors.grey100};
    width: 100%;
    padding: 0 16px 15px;

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidthNum + 1}px) {
        margin: 0 12px 0 0;
        font-size: 14px;
        line-height: 21px;
    }
`;

const EstimateNameContainer = styled.div`
    flex-grow: 3;
    white-space: nowrap;
    overflow: visible;
    display: flex;
    justify-content: space-between;
    flex-direction: row-reverse;
`;

const Estimate = styled.div`
    display: flex;
    > div {
        margin: 0 5px;
    }
`;

const LockText = styled.span`
    display: inline-block;
    margin-left: 5px;
`;
